import { useCallback, useEffect, useMemo, useState } from "react";
import { FaPaperclip, FaArchive, FaUpload, FaFileAlt, FaDownload, FaExpand, FaBook, FaInbox, FaStar, FaRegStar, FaSlidersH } from "react-icons/fa";
import { DOCUMENTS_CONSTANTS } from "../_MODULES/_API";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { Card, EmptyState, Skeleton, Badge } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { FieldFile, FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { onApiAction } from "../../../../_UI/onApiAction";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { API_CORE } from "../../../../../_CORE/API/ApiCORE";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { AdvancedFilterPanel } from "../../../../_UI/AdvancedFilterPanel";
import { ViewPagination } from "../../../../_UI/ViewPagination";
import { usePagedList } from "../../../../_UI/usePagedList";
import { UniversalFilePreviewModal } from "../../../../_UI/UniversalFilePreview";
import { PrivateBlobMedia } from "../../../../_UI/PrivateBlobMedia";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { onSilentPatch } from "../../../../_UI/onSilentPatch";
import { downloadFilesAsZip } from "../../../../_UI/FileBatchDownload";
import "../../../../_UI/_Styles/FileCollection.css";

export const PanelDocuments = ({ subProjectId }) => {

    const [useFiles, setFiles] = useState([]);
    const [useIsUploading, setIsUploading] = useState(false);
    const [usePreviewDocument, setPreviewDocument] = useState(null);
    const [useSearch, setSearch] = useState("");
    const [useAdvancedOpen, setAdvancedOpen] = useState(false);
    const [useFavoriteFilter, setFavoriteFilter] = useState("ALL");
    const [useGroupingFilter, setGroupingFilter] = useState("ALL");
    const [useKindFilter, setKindFilter] = useState("ALL");
    const [useDownloadingGroupId, setDownloadingGroupId] = useState(null);

    const { apiGet: onGetDocuments, useApiData: DOCUMENTS, useApiLoading: IS_LOADING, mutateLocal } = useApiQuery({
        minTimeQuery: 250, enablePagination: false, startLoading: true,
    });

    const { actionMutate } = useApiActionMutation({ isToastError: true, isToastSuccess: true, minTimeMutate: 300 });

    const onLoadDocuments = () => onGetDocuments({
        endpoint: DOCUMENTS_CONSTANTS.BY_SUBPROJECT, isDetail: true, config: { pathParams: { subProjectId } },
    });

    useEffect(() => { onLoadDocuments(); }, [subProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

    const ALL_ITEMS = [...(DOCUMENTS ?? [])].sort((a, b) => (Number(!!b.isFavorite) - Number(!!a.isFavorite)) || (Number(a.position ?? 0) - Number(b.position ?? 0)) || String(b.createdAt ?? "").localeCompare(String(a.createdAt ?? "")));

    const ITEMS = ALL_ITEMS
        .filter((document) => useFavoriteFilter === "ALL" || (!!document.isFavorite === (useFavoriteFilter === "YES")))
        .filter((document) => useGroupingFilter === "ALL" || (useGroupingFilter === "LINKED" ? !!document.logbookId : !document.logbookId))
        .filter((document) => useKindFilter === "ALL" || String(document.kind || "") === useKindFilter)
        .filter((document) => onMatchesSearch(useSearch, [
            document.title, document.originalName, document.description, document.logbook?.title, document.kind,
        ]));

    const ADVANCED_COUNT = [useFavoriteFilter !== "ALL", useGroupingFilter !== "ALL", useKindFilter !== "ALL"].filter(Boolean).length;
    const KIND_OPTIONS = [{ value: "ALL", label: "Todos los tipos" }, ...Array.from(new Set(ALL_ITEMS.map((document) => String(document.kind || "").trim()).filter(Boolean))).sort().map((value) => ({ value, label: value }))];

    const GROUPS = useMemo(() => {

        const LOOSE = [];
        const BY_ENTRY = new Map();

        ITEMS.forEach((document) => {

            if (!document.logbookId) {
                LOOSE.push(document);
                return;
            }

            if (!BY_ENTRY.has(document.logbookId)) {
                BY_ENTRY.set(document.logbookId, {
                    id: document.logbookId,
                    title: document.logbook?.title ?? "Entrada de bitácora",
                    documents: [],
                });
            }

            BY_ENTRY.get(document.logbookId).documents.push(document);
        });

        return [
            ...[...BY_ENTRY.values()].map((entry) => ({ ...entry, icon: <FaBook /> })),
            { id: "loose", title: "Sin entrada asociada", documents: LOOSE, icon: <FaInbox /> },
        ].filter((group) => group.documents.length > 0);

    }, [ITEMS]);

    const PAGER = usePagedList(GROUPS, 6, "filesPage");

    const onUpload = async () => {

        if (useFiles.length === 0) return;

        setIsUploading(true);

        const FORM_DATA = new FormData();
        FORM_DATA.append("subProjectId", subProjectId);
        FORM_DATA.append("title", "Archivo del subproyecto");
        useFiles.forEach((file) => FORM_DATA.append("files[]", file));

        const RESPONSE = await API_CORE.SERVICES.POST_RESOURCE(
            API_CORE.ENDPOINTS[DOCUMENTS_CONSTANTS.UPLOAD_MULTIPLE], FORM_DATA,
        );

        setIsUploading(false);

        if (RESPONSE?.success === false) return;

        const SAVED = Array.isArray(RESPONSE?.data)
            ? RESPONSE.data
            : (Array.isArray(RESPONSE) ? RESPONSE : []);

        SAVED.forEach((document) => mutateLocal({ insert: document, position: "end" }));
        setFiles([]);
    };

    const onLoadDocumentBlob = useCallback(async (document) => {
        if (!document?.id) throw new Error("Archivo sin identificador");
        const URL_RESOURCE = API_CORE.ENDPOINTS[DOCUMENTS_CONSTANTS.CONTENT].replace(":id", document.id);
        const BLOB = await API_CORE.SERVICES.GET_RESOURCE(URL_RESOURCE, { responseType: "blob" });
        if (!(BLOB instanceof Blob)) throw new Error("No se pudo obtener el archivo");
        return BLOB;
    }, []);

    const onDownload = async (document) => {
        if (!document?.id) return;
        const URL_RESOURCE = API_CORE.ENDPOINTS[DOCUMENTS_CONSTANTS.DOWNLOAD].replace(":id", document.id);
        const BLOB = await API_CORE.SERVICES.GET_RESOURCE(URL_RESOURCE, { responseType: "blob" });
        if (!(BLOB instanceof Blob)) return;

        const URL_OBJECT = window.URL.createObjectURL(BLOB);
        const LINK = window.document.createElement("a");

        LINK.href = URL_OBJECT;
        LINK.download = document.originalName || document.title || "archivo";
        window.document.body.appendChild(LINK);
        LINK.click();
        window.document.body.removeChild(LINK);
        window.URL.revokeObjectURL(URL_OBJECT);
    };

    const onDownloadGroup = async (group) => {
        if (!group?.documents?.length || useDownloadingGroupId !== null) return;
        setDownloadingGroupId(group.id);
        try {
            await downloadFilesAsZip({
                files: group.documents,
                loadBlob: onLoadDocumentBlob,
                fileName: `${group.title || "archivos"}.zip`,
                successMessage: `Se descargaron ${group.documents.length} archivo${group.documents.length === 1 ? "" : "s"} del grupo.`,
                errorMessage: "No se pudieron descargar todos los archivos del grupo.",
            });
        } finally {
            setDownloadingGroupId(null);
        }
    };

    const onDelete = async (document) => {

        const IS_CONFIRMED = await confirm.danger("Archivar archivo", `Se archiva "${document.title}". Podés recuperarlo desde Archivados.`);

        if (!IS_CONFIRMED) return;

        const RESPONSE = await onApiAction({
            method: "PATCH", endpoint: DOCUMENTS_CONSTANTS.ARCHIVE_ONE,
            pathParams: { id: document.id }, successMessage: "Archivo archivado",
        });

        if (RESPONSE.success) mutateLocal({ id: document.id, remove: true });
    };

    const onToggleFavorite = async (document) => {
        const previous = !!document.isFavorite;
        const next = !previous;
        mutateLocal({ id: document.id, patch: { isFavorite: next } });
        const RESPONSE = await onSilentPatch({
            endpoint: DOCUMENTS_CONSTANTS.UPDATE, id: document.id, data: { isFavorite: next },
            errorMessage: "No se pudo guardar el destacado del archivo.",
        });
        if (!RESPONSE.success) {
            mutateLocal({ id: document.id, patch: { isFavorite: previous } });
            return;
        }
        mutateLocal({ id: document.id, patch: { ...RESPONSE.data, isFavorite: next } });
    };

    const onOpenViewer = (document) => setPreviewDocument(document);

    return (
        <>
            <div className="LStudio__app_documents_upload">

                <FieldFile id="subproject_files" label="Subir archivos" files={useFiles} onChange={setFiles}
                    dropText="Arrastrá imágenes, videos o documentos" maxFiles={30} span={2} />

                <div className="LStudio__app_documents_upload_actions">
                    <AppButton icon={<FaUpload />} isLoading={useIsUploading}
                        isDisabled={useFiles.length === 0} onClick={onUpload}>
                        Subir {useFiles.length > 0 ? `(${useFiles.length})` : ""}
                    </AppButton>
                </div>

            </div>

            {
                ALL_ITEMS.length > 0 && (
                    <div className="LStudio__app_panel_bar">
                        <ViewToolbar
                            filters={<>
                                <SearchFilter value={useSearch} onChange={setSearch} total={ALL_ITEMS.length} matches={ITEMS.length}
                                    placeholder="Ej.: contrato.pdf, captura o deploy..." />
                                <AppButton variant={useAdvancedOpen ? "soft" : "outline"} tone="neutral" icon={<FaSlidersH />}
                                    onClick={() => setAdvancedOpen((current) => !current)}>
                                    {ADVANCED_COUNT ? `Avanzado (${ADVANCED_COUNT})` : "Avanzado"}
                                </AppButton>
                            </>}
                            expandedContent={<AdvancedFilterPanel isOpen={useAdvancedOpen} activeCount={ADVANCED_COUNT}
                                onClear={() => { setFavoriteFilter("ALL"); setGroupingFilter("ALL"); setKindFilter("ALL"); }}>
                                <FieldSelect id="documents_adv_favorite" label="Destacado" value={useFavoriteFilter} onChange={(event) => setFavoriteFilter(event.target.value)} options={[{ value: "ALL", label: "Todos" }, { value: "YES", label: "Destacados" }, { value: "NO", label: "No destacados" }]} />
                                <FieldSelect id="documents_adv_group" label="Agrupación" value={useGroupingFilter} onChange={(event) => setGroupingFilter(event.target.value)} options={[{ value: "ALL", label: "Todos" }, { value: "LINKED", label: "Con entrada de bitácora" }, { value: "LOOSE", label: "Sin entrada asociada" }]} />
                                <FieldSelect id="documents_adv_kind" label="Tipo" value={useKindFilter} onChange={(event) => setKindFilter(event.target.value)} options={KIND_OPTIONS} isSearchable />
                            </AdvancedFilterPanel>}
                            total={ITEMS.length} perPage={PAGER.usePerPage} onChangePerPage={PAGER.onChangePerPage}
                            exportColumns={[
                                { key: "originalName", label: "Archivo" },
                                { key: "kind", label: "Tipo" },
                                { key: "size", label: "Tamaño (KB)" },
                                { key: "logbook", label: "Entrada" },
                            ]}
                            exportRows={ITEMS.map((document) => ({
                                originalName: document.originalName,
                                kind: document.kind,
                                size: (Number(document.size) / 1024).toFixed(0),
                                logbook: document.logbook?.title ?? "",
                            }))}
                            exportName="archivos" />
                    </div>
                )
            }

            {IS_LOADING && <Skeleton variant="box" height={200} />}

            {
                !IS_LOADING && ITEMS.length === 0 && (
                    <EmptyState icon={<FaPaperclip />} title="Sin archivos todavía"
                        description="Los que subas desde una entrada de bitácora aparecen agrupados por esa entrada." />
                )
            }

            {
                !IS_LOADING && PAGER.paged.map((group) => (
                    <Card key={group.id} className="LStudio__app_documents_group">

                        <div className="LStudio__app_documents_group_head">

                            {group.icon}

                            <span className="LStudio__app_documents_group_title">{group.title}</span>

                            <Badge tone="neutral" size="sm">{group.documents.length}</Badge>

                            <AppButton size="sm" variant="ghost" tone="neutral" icon={<FaDownload />}
                                isLoading={String(useDownloadingGroupId) === String(group.id)}
                                isDisabled={useDownloadingGroupId !== null && String(useDownloadingGroupId) !== String(group.id)}
                                onClick={() => onDownloadGroup(group)}>
                                Descargar todos
                            </AppButton>

                        </div>

                        <div className="LStudio__app_documents_grid LStudio__app_file_scroll_area">
                            {
                                group.documents.map((document) => (
                                    <figure key={document.id} className="LStudio__app_documents_item">

                                        <div className="LStudio__app_documents_item_preview">
                                            {
                                                document.kind === "IMAGE"
                                                    ? <PrivateBlobMedia file={document} loadBlob={onLoadDocumentBlob} alt={document.title} />
                                                    : (document.mimetype ?? "").startsWith("video/")
                                                        ? <PrivateBlobMedia file={document} loadBlob={onLoadDocumentBlob} video />
                                                        : <FaFileAlt aria-hidden="true" />
                                            }
                                        </div>

                                        <figcaption className="LStudio__app_documents_item_caption">
                                            <span className="LStudio__app_documents_item_title">
                                                {document.isFavorite && <FaStar className="LStudio__app_documents_item_star" title="Destacado" />}
                                                {document.originalName}
                                            </span>
                                            <span className="LStudio__app_documents_item_name">
                                                {(Number(document.size) / 1024).toFixed(0)} KB
                                            </span>
                                        </figcaption>

                                        <div className="LStudio__app_documents_item_actions">
                                            <button type="button" className="LStudio__app_documents_item_action"
                                                onClick={() => onOpenViewer(document)} aria-label="Vista previa" title="Vista previa">
                                                <FaExpand />
                                            </button>

                                            <CardActionMenu
                                                title="Opciones del archivo"
                                                ariaLabel={`Opciones de ${document.originalName}`}
                                                items={[
                                                    {
                                                        key: "favorite",
                                                        icon: document.isFavorite ? <FaRegStar /> : <FaStar />,
                                                        label: document.isFavorite ? "Quitar destacado" : "Destacar",
                                                        onClick: () => onToggleFavorite(document),
                                                    },
                                                    { key: "download", icon: <FaDownload />, label: "Descargar", onClick: () => onDownload(document) },
                                                    { divider: true },
                                                    { key: "archive", icon: <FaArchive />, label: "Archivar", tone: "danger", onClick: () => onDelete(document) },
                                                ]}
                                            />
                                        </div>

                                    </figure>
                                ))
                            }
                        </div>

                    </Card>
                ))
            }

            <ViewPagination page={PAGER.usePage} totalPages={PAGER.totalPages}
                total={PAGER.total} perPage={PAGER.usePerPage} onChange={PAGER.onChangePage} />

            <UniversalFilePreviewModal
                file={usePreviewDocument}
                isOpen={!!usePreviewDocument}
                onClose={() => setPreviewDocument(null)}
                loadBlob={onLoadDocumentBlob}
                onDownload={onDownload}
            />
        </>
    );
};
